'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import { cn } from '@/utils/cn';

interface Props {
  message: string | ReactNode;
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  open?: boolean;
  onClose?: () => void;
  className?: string;
}

export const InfoModal = ({
  message,
  title,
  icon,
  action,
  open: controlledOpen,
  onClose,
  className,
}: Props) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const modalRef = useRef<HTMLDivElement>(null);

  const close = () => {
    if (isControlled) onClose?.();
    else setInternalOpen(false);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        close();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      {!isControlled && (
        <button
          onClick={() => setInternalOpen(true)}
          className={'hover:text-white text-zinc-400 transition-colors ease-in-out duration-150'}
        >
          <Info size={18} />
        </button>
      )}
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex justify-center p-4 z-40 overflow-y-auto">
          <span
            ref={modalRef}
            className={cn(
              'bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-md max-h-[calc(100dvh-2rem)] my-auto rounded-xl shadow-xl flex flex-col p-8 gap-4',
              className
            )}
          >
            <span className="flex justify-between items-center shrink-0">
              <span className="flex items-center gap-4">
                {icon}
                <span className="text-xl font-semibold">{title}</span>
              </span>
              <button onClick={close} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto -mr-4 pr-4">
              {typeof message === 'string' ? (
                <span className="text-zinc-400 text-left">{message}</span>
              ) : (
                message
              )}
            </span>
            {action && (
              <span className={'flex flex-col gap-4 shrink-0'} onClick={close}>
                {action}
              </span>
            )}
            {!action && (
              <span className={'flex flex-col gap-4 shrink-0'}>
                <Button isPrimary={true} onClick={close}>
                  Close
                </Button>
              </span>
            )}
          </span>
        </span>
      )}
    </>
  );
};
